import React from 'react'
import styled from 'styled-components'
import 'react-datepicker/dist/react-datepicker.css'
import ja from 'date-fns/locale/ja'

import DatePicker, { registerLocale } from 'react-datepicker'
import { CalendarIcon } from './icons/CalendarIcon'

import { DateTimeInputProps, DateTimeInputContainerProps } from './DateTimeInput.types'

import { styleTheme } from '../../../style/theme'

registerLocale('ja', ja)

export const DateTimeInput: React.FC<DateTimeInputContainerProps> = (props) => {
  return <Component {...props} />
}

const Component: React.FC<DateTimeInputProps> = ({
  showTimeSelect,
  showMonthYearPicker,
  onChange,
  openToDate,
  includeDates,
  selected,
  dateFormat,
  placeholder,
  disabled,
  readonly,
  className,
}) => {
  return (
    <div>
      <DatePicker
        locale="ja"
        dateFormat={dateFormat || showTimeSelect ? 'yyyy/MM/dd HH:mm' : showMonthYearPicker ? 'yyyy/MM' : 'yyyy/MM/dd'}
        customInput={<CustomInput readonly={readonly} />}
        placeholderText={
          placeholder || (showMonthYearPicker ? '年月を選択' : showTimeSelect ? '日時を選択' : '日付を選択')
        }
        calendarContainer={StyledCalendarContainer}
        showTimeSelect={showTimeSelect}
        showMonthYearPicker={showMonthYearPicker}
        timeCaption="ご予約可能時間"
        openToDate={openToDate}
        includeDates={includeDates}
        dateFormatCalendar="yyyy年 M月"
        onChange={onChange}
        selected={selected}
        disabled={disabled}
        className={className}
      />
    </div>
  )
}

const StyledCalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  border-color: #d9d9d9;
  padding: 24px;

  @media screen and (min-width: 768px) {
    flex-direction: row;
  }

  .react-datepicker {
    &__navigation--previous {
      transform: translate(63px, 22px);
    }

    &__navigation--next {
      right: 0;
      top: 24px;
      left: 228px;
    }

    &__month-container {
      position: relative;
      width: 280px;
    }

    &__month {
      margin: 0;
    }

    &__day {
      margin: 5px;
      color: ${styleTheme.colors.black};

      &[aria-label$='土曜日'] {
        color: #1e78ff;
      }

      &[aria-label$='日曜日'] {
        color: #e56274;
      }

      &[aria-disabled='true'][aria-label$='土曜日'],
      &[aria-disabled='true'][aria-label$='日曜日'] {
        color: #eee;
      }

      &--outside-month {
        visibility: hidden;
      }

      &--keyboard-selected,
      &--selected {
        color: #fff !important;
        background-color: #1babbc;
      }

      &--disabled {
        color: #eee;
      }

      &[aria-disabled='false']:hover {
        color: #fff;
        background-color: #1babbc;
      }
    }

    &__time-container {
      width: auto;
      border-top: 1px solid #eee;
      border-left: none;
      padding-top: 12px;
      margin-left: 0;

      @media screen and (min-width: 768px) {
        padding-left: 24px;
        border-top: none;
        border-left: 1px solid #eee;
        margin-left: 24px;
      }
    }

    &__time-box {
      width: 280px !important;
    }

    &__time-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      &-item {
        padding: 0 !important;
        border: 1px solid #1babbc;
        color: #1babbc;
        width: 64px;
        line-height: 30px;
        border-radius: 4px;

        :hover {
          background-color: #1babbc !important;
          color: #fff;
        }
      }
    }

    &__header {
      background-color: #fff;
      border-bottom: none;

      &--time {
        padding-bottom: 20px;
      }
    }

    &__current-month {
      margin-bottom: 12px;
    }

    &-time__header {
      color: ${styleTheme.colors.black};
      text-align: left;
      font-weight: normal;
      font-size: 12px;
      overflow: visible;
    }

    &__current-month {
      color: ${styleTheme.colors.black};
    }

    &__day-name {
      margin: 5px;
      color: ${styleTheme.colors.black};
    }
  }
`

const CustomInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { readonly?: boolean }
>((props, ref) => {
  return (
    <StyledCustomInputWrapper>
      <CalendarIcon />
      <StyledInput ref={ref} {...props} readOnly={props.readonly} />
    </StyledCustomInputWrapper>
  )
})

const StyledCustomInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const StyledInput = styled.input`
  background-color: ${styleTheme.colors.white};
  padding: 12px 16px 12px 40px;
  border-radius: 6px;
  border: 1px solid ${styleTheme.colors.gray.base};
  line-height: 1.5;
  color: ${styleTheme.colors.black};
  font-size: 16px;
  width: 220px;
  cursor: pointer;
  user-select: none;

  &::placeholder {
    color: ${styleTheme.colors.gray.base};
  }
`
